import { useRef, useState, useEffect, useCallback } from 'react';
import { TickerItem, severityConfig } from '../services/serviceHealthTypes';

interface TickerBarProps {
  items: TickerItem[];
  totalCount: number;
  onItemClick: (item: TickerItem) => void;
  speed?: number;  // pixels per second
}

// Vendor brand colors for icon backgrounds
const vendorColors: Record<string, string> = {
  microsoft365: '#0078d4',
  azure: '#0089d6',
  connectwise: '#e31937',
  datto: '#199ed9',
  itglue: '#00a4e4',
};

// Clean up redundant vendor names from titles
function cleanTitle(title: string, vendor: string): string {
  const vendorPrefixes: Record<string, string[]> = {
    datto: ['Datto ', 'Kaseya '],
    connectwise: ['ConnectWise '],
    itglue: ['ITGlue ', 'IT Glue '],
    microsoft365: ['Microsoft 365 ', 'M365 ', 'Office 365 '],
    azure: ['Azure '],
  };

  let cleaned = title;
  const prefixes = vendorPrefixes[vendor] || [];
  for (const prefix of prefixes) {
    if (cleaned.startsWith(prefix)) {
      cleaned = cleaned.slice(prefix.length);
      break;
    }
  }
  return cleaned;
}

// Default scroll speed: pixels per second
const DEFAULT_SPEED = 75;

export default function TickerBar({ items, totalCount, onItemClick, speed = DEFAULT_SPEED }: TickerBarProps) {
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const singleSetRef = useRef<HTMLDivElement>(null);

  // Animation state
  const [needsScroll, setNeedsScroll] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  const positionRef = useRef(0);
  const lastTimeRef = useRef(0);
  const lastRenderPosRef = useRef(0);
  const rafRef = useRef<number>(0);

  // Calculate scroll distance
  useEffect(() => {
    if (singleSetRef.current && containerRef.current) {
      const singleSetWidth = singleSetRef.current.offsetWidth;
      const containerWidth = containerRef.current.offsetWidth;

      if (singleSetWidth > containerWidth) {
        setNeedsScroll(true);
        setScrollDistance(singleSetWidth);
      } else {
        setNeedsScroll(false);
        setScrollDistance(0);
      }
    }
  }, [items]);

  // Smooth animation loop using requestAnimationFrame
  useEffect(() => {
    if (!needsScroll || !contentRef.current || scrollDistance === 0) {
      return;
    }

    const animate = (timestamp: number) => {
      if (!contentRef.current) return;

      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      if (!isPaused) {
        const deltaTime = timestamp - lastTimeRef.current;
        const deltaPixels = (speed * deltaTime) / 1000;

        positionRef.current -= deltaPixels;

        // Reset when we've scrolled one full set
        if (positionRef.current <= -scrollDistance) {
          positionRef.current += scrollDistance;
        }

        // Only update DOM when position changes by at least 1 pixel
        const roundedPos = Math.round(positionRef.current);
        if (roundedPos !== lastRenderPosRef.current) {
          lastRenderPosRef.current = roundedPos;
          contentRef.current.style.transform = `translateX(${roundedPos}px)`;
        }
      }

      lastTimeRef.current = timestamp;
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [needsScroll, scrollDistance, speed, isPaused]);

  const handleItemClick = useCallback((e: React.MouseEvent, item: TickerItem) => {
    e.stopPropagation();
    onItemClick(item);
  }, [onItemClick]);

  // If no active incidents, show "All Systems Operational"
  if (items.length === 0) {
    return (
      <div className="ticker-bar ticker-bar-operational">
        <div className="ticker-content-static">
          <span className="ticker-all-ok">
            <span className="ticker-ok-icon">&#10003;</span>
            All Vendor Systems Operational
          </span>
        </div>
      </div>
    );
  }

  const formatClients = (clients?: string[]) => {
    if (!clients || clients.length === 0) return null;
    if (clients.length <= 2) return clients.join(', ');
    return `${clients.slice(0, 2).join(', ')} +${clients.length - 2} more`;
  };

  const renderItems = (keyPrefix: string) =>
    items.map((item, index) => {
      const itemNum = index + 1;
      return (
        <button
          key={`${keyPrefix}-${item.id}-${index}`}
          className={`ticker-item ticker-item-${item.vendor}`}
          onClick={(e) => handleItemClick(e, item)}
        >
          <span className="ticker-item-number">{itemNum}/{totalCount}</span>
          <span
            className="ticker-vendor-icon"
            style={{ backgroundColor: vendorColors[item.vendor] || '#666' }}
          >
            {item.vendorIcon}
          </span>
          <span className="ticker-item-title">
            {cleanTitle(item.title, item.vendor)}
            {item.affectedClients && item.affectedClients.length > 0 && (
              <span className="ticker-clients">({formatClients(item.affectedClients)})</span>
            )}
          </span>
          <span
            className={`ticker-severity ticker-severity-${item.severity}`}
            style={{
              backgroundColor: severityConfig[item.severity].bgColor,
              color: severityConfig[item.severity].color
            }}
          >
            {item.status}
          </span>
        </button>
      );
    });

  return (
    <div className="ticker-bar-wrapper">
      <div
        className="ticker-bar"
        ref={containerRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          className={`ticker-content ${!needsScroll ? 'no-scroll' : 'js-animated'}`}
          ref={contentRef}
        >
          {/* First set - used to measure width */}
          <div className="ticker-set" ref={singleSetRef}>
            {renderItems('a')}
          </div>
          {/* Second set - for seamless loop */}
          {needsScroll && (
            <div className="ticker-set">
              {renderItems('b')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
